import { MOCK_API_URL, SEEK_ENV } from 'src/config';
import type { AppConfig } from 'src/modules/appConfig/types';
import { classifyError } from 'src/modules/error-helper';
import { log } from 'src/modules/error-logger';
import type { ApiClient } from 'src/types';
import * as Schema from 'src/types/graphql';

import {
  uploadResumeSuccess,
  uploadResumeFailure,
  getResumeLinkSuccess,
  getResumeLinkFailure,
  updateResumeSuccess,
  updateResumeFailure,
} from './actionCreators';
import { Actions } from './reducer';
import type { UploadResume } from './types';
import { prepareResumeDataForUpload } from './upload';

export const makeResumeUploadUpdater = ({
  api: { queryGql, mutateGql },
  appConfig: { language, locale, zone },
}: {
  api: ApiClient;
  appConfig: AppConfig;
}) => ({
  on: Actions.RESUME_UPLOAD_START,
  run: async ({ file, isDefault, id }: UploadResume) => {
    const documentUploadData = await queryGql({
      query: Schema.GetDocumentUploadDataDocument,
      variables: {
        id,
      },
      fetchPolicy: 'no-cache',
    });

    if (documentUploadData.error) {
      documentUploadData.error.name = `Resume error (Fetch upload data): ${documentUploadData.error.name}`;
      log(documentUploadData.error, { name: 'Resume Upload' });
    }

    const { url, formData } = prepareResumeDataForUpload({
      data: documentUploadData?.data,
      file,
      environment: SEEK_ENV || 'staging',
      mockApiUrl: MOCK_API_URL,
    });

    try {
      await fetch(url, { method: 'POST', body: formData });
    } catch (error) {
      if (error instanceof Error) {
        error.name = `Resume error (Document upload): ${error.name}`;
        log(error, {
          name: 'Resume Upload',
          input: {
            key: documentUploadData.data.viewer?.documentUploadFormData?.key,
            size: file.size,
            type: file.type,
          },
        });
        throw error;
      }
    }

    const uploadResponse = await mutateGql({
      mutation: Schema.ProcessUploadedResumeDocument,
      variables: {
        resumeData: {
          id,
          isDefault,
          parsingContext: {
            id,
          },
          countryCode: locale,
          origin: Schema.ResumeOriginType.Uploaded,
          zone,
        },
        languageCode: language,
      },
    });

    return {
      newResumeId: uploadResponse?.data?.processUploadedResume?.resume.id,
      isDefault,
    };
  },
  success: uploadResumeSuccess,
  failure: uploadResumeFailure,
  classifyError,
});

export const makeGetLinkResumeUpdater = ({
  api: { queryGql },
}: {
  api: ApiClient;
}) => ({
  on: Actions.RESUME_GET_LINK_START,
  run: (id: string) =>
    queryGql({
      query: Schema.DocumentDownloadLinkDocument,
      variables: {
        resourceId: id,
        resumeId: id,
      },
      fetchPolicy: 'no-cache',
    }).then((resp) => ({
      link: resp?.data?.viewer?.documentDownloadLink?.link,
      id,
      resume: resp?.data?.viewer?.resume,
    })),
  success: getResumeLinkSuccess,
  failure: getResumeLinkFailure,
  classifyError,
});

export const makeUpdateResumeUpdater = ({
  api: { queryGql },
}: {
  api: ApiClient;
}) => ({
  on: Actions.RESUME_UPDATE_START,
  run: (id: string) =>
    queryGql({
      query: Schema.GetResumeByIdDocument,
      variables: {
        id,
      },
      fetchPolicy: 'no-cache',
    }).then((resp) => resp?.data?.viewer?.resume),
  success: updateResumeSuccess,
  failure: updateResumeFailure,
  classifyError,
});
